import type { FormEvent, ReactNode } from "react";
import { Select, StatusBadge, TextArea, TextInput } from "@no-busy-work/ui";
import type { NbwStatus, SelectOption } from "@no-busy-work/ui";
import { UI_TONE } from "../OpsCenter.Config";
import type { OpsCenterState, UiTone } from "../OpsCenter.Types";

export function SectionHeader({ title, status, tone = UI_TONE.Neutral }: { readonly title: string; readonly status?: string; readonly tone?: UiTone }): ReactNode {
  return (
    <div className="section-header">
      <h2>{title}</h2>
      {status ? <StatusPill label={status} tone={tone} /> : null}
    </div>
  );
}

export function StatusPill({ label, tone }: { readonly label: string; readonly tone: UiTone }): ReactNode {
  return <StatusBadge label={label} status={statusForTone(tone)} />;
}

export function Metric({ label, value }: { readonly label: string; readonly value: string }): ReactNode {
  return (
    <div className="metric">
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

export function Messages({ state }: { readonly state: OpsCenterState }): ReactNode {
  return (
    <>
      {state.notice ? <p className="notice-message">{state.notice}</p> : null}
      {state.error ? <p className="auth-error">{state.error}</p> : null}
    </>
  );
}

export function TextField({ label, name, type, value, autoComplete = "off", onChange }: { readonly label: string; readonly name: string; readonly type: string; readonly value: string; readonly autoComplete?: string; readonly onChange: (value: string) => void }): ReactNode {
  return (
    <TextInput
      autoComplete={autoComplete}
      inputId={name}
      label={label}
      name={name}
      type={type}
      value={value}
      onChange={(event) => onChange(event.currentTarget.value)}
    />
  );
}

export function TextAreaField({ label, name, rows, value, onChange }: { readonly label: string; readonly name: string; readonly rows: number; readonly value: string; readonly onChange: (value: string) => void }): ReactNode {
  return (
    <TextArea
      inputId={name}
      label={label}
      name={name}
      rows={rows}
      value={value}
      onChange={(event) => onChange(event.currentTarget.value)}
    />
  );
}

export function SelectField({ label, name, options, value, onChange }: { readonly label: string; readonly name: string; readonly options: readonly (readonly [string, string])[]; readonly value: string; readonly onChange: (value: string) => void }): ReactNode {
  return (
    <Select
      inputId={name}
      label={label}
      name={name}
      options={toSelectOptions(options)}
      value={value}
      onChange={(event) => onChange(event.currentTarget.value)}
    />
  );
}

export function formatLabel(template: string, values: Record<string, string>): string {
  return Object.entries(values).reduce((result, [key, value]) => result.replaceAll(`{${key}}`, value), template);
}

export function submit(event: FormEvent<HTMLFormElement>, action: () => Promise<void>): void {
  event.preventDefault();
  void action();
}

function statusForTone(tone: UiTone): NbwStatus {
  if (tone === UI_TONE.Ok) {
    return "success";
  }
  if (tone === UI_TONE.Warn) {
    return "warning";
  }
  if (tone === UI_TONE.Danger || tone === UI_TONE.Critical) {
    return "danger";
  }
  return "neutral";
}

function toSelectOptions(options: readonly (readonly [string, string])[]): readonly SelectOption[] {
  return options.map(([value, label]) => ({ label, value }));
}
